/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import { useHistory } from "react-router-dom";
import powerDialerStore from "Stores/powerDialerStore";
import userDetail from "Stores/userDetail";
import { formatPhoneNumberIntl } from "react-phone-number-input";
import { STATES, OUTCOMES, contactClock } from "Utils/powerDialerMachine";
import { askLeave } from "./leaveGuard";

const PHONE_LABEL = { phone: "Phone", mobilePhone: "Mobile", otherPhone: "Other" };
const SETTINGS_URL = "/settings/dialer/voicemail";

const fmt = (sec = 0) => {
  const s = Math.max(0, Math.round(sec));
  return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
};

// Re-renders every second so live timers move.
const useNow = () => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  return now;
};

const chipFor = (row = {}) => {
  if (row.notLogged) return ["na", "Not logged"];
  if (row.voicemailDropped) return ["vm", row.autoLogged ? "Auto voicemail dropped" : "Voicemail dropped"];
  if (row.outcome === OUTCOMES.VOICEMAIL_NONE_LEFT || row.outcome === OUTCOMES.LEFT_VOICEMAIL) return ["vm", row.outcome];
  if (row.outcome === OUTCOMES.WRONG_NUMBER) return ["warn", row.outcome];
  if (row.connected) return ["ok", row.outcome];
  return ["na", row.outcome];
};

const Kbd = ({ children, light = false }) => <span className={`pdKbd ${light ? "light" : ""}`}>{children}</span>;

const Who = ({ item = {} }) => (
  <div className="pdWho">
    <div className="pdInitial">{(item.name || "?").charAt(0).toUpperCase()}</div>
    <div className="pdWhoText">
      <div className="pdWhoName">{item.name || "Unnamed contact"}</div>
      <div className="pdWhoNum">
        {item.phone ? formatPhoneNumberIntl(item.phone) || item.phone : "No number"}
        {item.phone && ` · ${PHONE_LABEL[item.phoneType] || "Phone"}`}
      </div>
    </div>
  </div>
);

const Progress = observer(() => {
  const now = useNow();
  const { counts, dialableCount, currentCall } = powerDialerStore;
  const live = currentCall?.connectedAt && !currentCall?.endedAt ? (now - currentCall.connectedAt) / 1000 : 0;
  const total = Math.max(dialableCount, 1);
  const segments = [
    ["ok", counts.connected],
    ["vm", counts.voicemail],
    ["na", counts.noAnswer + counts.busy],
    ["warn", counts.wrongNumber]
  ];
  return (
    <div className="pdProgress">
      <div className="pdProgressTop">
        <span>
          <b>
            {Math.min(counts.called, dialableCount)} of {dialableCount}
          </b>{" "}
          called
        </span>
        <span className="pdMuted">Talk {fmt(counts.talkSeconds + live)}</span>
      </div>
      <div className="pdProgressBar">
        {segments.map(([kind, n]) =>
          n > 0 ? (
            <i key={kind} className={kind} style={{ "--pdProgress": `${(n / total) * 100}%` }} />
          ) : null
        )}
      </div>
      <div className="pdTally">
        <span>
          <b>{counts.connected}</b> connected
        </span>
        <span>
          <b>{counts.voicemail}</b> voicemail
        </span>
        <span>
          <b>{counts.noAnswer + counts.busy}</b> no answer
        </span>
        <span>
          <b>{counts.wrongNumber}</b> wrong number
        </span>
      </div>
    </div>
  );
});

const Dialing = observer(() => {
  const now = useNow();
  const { current, currentCall } = powerDialerStore;
  const finding = current?.status === "finding";
  return (
    <div className="pdNow live">
      <Who item={current} />
      <div className="pdStatus">
        <span className="pdStatusText">{finding ? "Finding number…" : "Ringing…"}</span>
        {!finding && currentCall && <span className="pdTimer">{fmt((now - currentCall.startedAt) / 1000)}</span>}
      </div>
    </div>
  );
});

const Connected = observer(() => {
  const now = useNow();
  const [showNotes, setShowNotes] = useState(false);
  const { current, currentCall, muted, notes } = powerDialerStore;
  return (
    <div className="pdNow live">
      <Who item={current} />
      <div className="pdStatus">
        <span className="pdStatusText ok">Connected</span>
        <span className="pdTimer">{fmt((now - (currentCall?.connectedAt || now)) / 1000)}</span>
        {userDetail?.userFeatureFlag?.recordingEnabled && (
          <span className="pdRec">
            <i />
            Recording
          </span>
        )}
      </div>
      <div className="pdAi">
        <span className="pdBars">
          <b />
          <b />
          <b />
          <b />
          <b />
        </span>
        AI is taking notes
      </div>
      <div className="pdCallBtns">
        <button
          type="button"
          className={`pdRound ${muted ? "on" : ""}`}
          aria-label={muted ? "Unmute" : "Mute"}
          aria-pressed={muted}
          onClick={powerDialerStore.toggleMute}
        >
          <i className="material-symbols-outlined">{muted ? "mic_off" : "mic"}</i>
        </button>
        <button type="button" className="pdRound end" aria-label="End call" onClick={powerDialerStore.hangup}>
          <i className="material-symbols-outlined">call_end</i>
        </button>
        <button
          type="button"
          className={`pdRound ${showNotes ? "on" : ""}`}
          aria-label="Notes"
          aria-pressed={showNotes}
          onClick={() => setShowNotes(!showNotes)}
        >
          <i className="material-symbols-outlined">edit_note</i>
        </button>
      </div>
      {showNotes && (
        <textarea
          className="pdNotes"
          aria-label="Notes"
          placeholder="Notes"
          value={notes}
          onChange={e => powerDialerStore.setNotes(e.target.value)}
        />
      )}
    </div>
  );
});

const Voicemail = observer(() => {
  const history = useHistory();
  const { current, defaultRecording, settings } = powerDialerStore;
  return (
    <div className="pdNow live">
      <Who item={current} />
      <div className="pdStatus">
        <span className="pdStatusText vm">
          <i className="material-symbols-outlined">voicemail</i>
          Reached voicemail
        </span>
        <span className="pdMuted">Not picked up</span>
      </div>
      <div className="pdBtnRow">
        <button type="button" className="pdPrimaryBtn" onClick={powerDialerStore.dropVoicemail}>
          <span className="pdBtnLabel" title={defaultRecording ? `Drop “${defaultRecording.name}”` : undefined}>
            {defaultRecording ? `Drop “${defaultRecording.name}”` : "Drop voicemail"}
          </span>
          <Kbd>V</Kbd>
        </button>
        <button type="button" className="pdPrimaryBtn out" onClick={powerDialerStore.skipVoicemail}>
          Skip <Kbd light>S</Kbd>
        </button>
      </div>
      {!settings.autoVoicemailDrop && (
        <div className="pdSub">
          Auto drop is off ·{" "}
          <button type="button" className="pdLink" onClick={() => history.push(SETTINGS_URL)}>
            Turn on
          </button>
        </div>
      )}
    </div>
  );
});

const talkOf = call => (call?.connectedAt ? ((call.endedAt || Date.now()) - call.connectedAt) / 1000 : 0);

const Wrapup = observer(() => {
  const { current, currentCall, dispositions, wrapupDispositionId, notes } = powerDialerStore;
  const s = currentCall?.suggestion;
  return (
    <div className="pdNow live">
      <div className="pdStatus">
        <span className="pdStatusTitle">
          {(current?.name || "").split(" ")[0]} · Connected {fmt(talkOf(currentCall))}
        </span>
      </div>
      {s?.summary && (
        <div className="pdSuggest">
          <i className="material-symbols-outlined">auto_awesome</i>
          Suggested from the call: {s.outcome} · {s.summary}
        </div>
      )}
      <div className="pdOutcomes" role="radiogroup" aria-label="Call outcome">
        {dispositions.slice(0, 8).map((d, i) => (
          <button
            type="button"
            key={d.id}
            role="radio"
            aria-checked={d.id === wrapupDispositionId}
            className={`pdOutcome ${d.id === wrapupDispositionId ? "selected" : ""}`}
            onClick={() => powerDialerStore.setWrapupDisposition(d.id)}
          >
            <span className="pdRadio" />
            <span className="pdOutcomeName" title={d.displayName}>
              {d.displayName}
            </span>
            <Kbd light>{i + 1}</Kbd>
          </button>
        ))}
      </div>
      <textarea
        className="pdNotes"
        aria-label="Notes"
        placeholder="Notes"
        value={notes}
        onChange={e => powerDialerStore.setNotes(e.target.value)}
      />
      <button type="button" className="pdPrimaryBtn" onClick={powerDialerStore.logWrapup}>
        Log &amp; next <Kbd>Enter</Kbd>
      </button>
    </div>
  );
});

const AutoLog = observer(() => {
  const { current, currentCall, autoLogLeft } = powerDialerStore;
  const s = currentCall?.suggestion || {};
  return (
    <div className="pdNow live">
      <div className="pdStatus">
        <span className="pdStatusTitle">
          {(current?.name || "").split(" ")[0]} · Connected {fmt(talkOf(currentCall))}
        </span>
        <span className="pdRing small">{autoLogLeft}</span>
      </div>
      <div className="pdSuggest">
        <i className="material-symbols-outlined">auto_awesome</i>
        Logging as
      </div>
      <div className="pdLoggingAs">
        <span className="pdChip ok">{s.outcome || OUTCOMES.CONNECTED}</span>
        <span>{s.summary}</span>
      </div>
      <div className="pdBtnRow">
        <button type="button" className="pdPrimaryBtn out" onClick={powerDialerStore.changeAutoLog}>
          Change <Kbd light>C</Kbd>
        </button>
        <button type="button" className="pdPrimaryBtn" onClick={powerDialerStore.logAutoNow}>
          Log now <Kbd>Enter</Kbd>
        </button>
      </div>
      <div className="pdSub">Confident enough to log for you. You can change it later in This session.</div>
    </div>
  );
});

const nextItem = () => {
  const { queue, dialFrom, upcomingIndex } = powerDialerStore;
  if (upcomingIndex > -1) return queue[upcomingIndex] || null;
  return queue.slice(dialFrom + 1).find(q => ["ready", "finding"].includes(q.status)) || null;
};

const Countdown = observer(() => {
  const { callLog, countdownLeft, current } = powerDialerStore;
  const last = current?.status === "ready" ? null : callLog[callLog.length - 1];
  const next = nextItem();
  return (
    <div className="pdNow">
      {last && (
        <div className="pdLastLogged">
          <i className="material-symbols-outlined">check_circle</i>
          {last.name} · {chipFor(last)[1]} · logged
        </div>
      )}
      <div className="pdCountdown">
        <span className="pdRing">{countdownLeft}</span>
        <div>
          <div className="pdSecTitle">Calling next</div>
          {next && <Who item={next} />}
        </div>
      </div>
      <div className="pdBtnRow">
        <button type="button" className="pdPrimaryBtn out" onClick={powerDialerStore.wait}>
          Wait <Kbd light>Esc</Kbd>
        </button>
        <button type="button" className="pdPrimaryBtn" onClick={powerDialerStore.dial}>
          Call now <Kbd>Enter</Kbd>
        </button>
      </div>
      <div className="pdSub">No answer or busy is logged for you</div>
    </div>
  );
});

const Paused = observer(() => {
  const next = nextItem();
  return (
    <div className="pdNow">
      <div className="pdStatusTitle">Paused</div>
      {next && <div className="pdSub">Next: {next.name}</div>}
      <button type="button" className="pdPrimaryBtn" onClick={powerDialerStore.dial}>
        <i className="material-symbols-outlined">play_arrow</i>
        Resume <Kbd>Enter</Kbd>
      </button>
    </div>
  );
});

const SessionList = observer(() => {
  const [open, setOpen] = useState(-1);
  const { callLog, dispositions } = powerDialerStore;
  if (!callLog.length) return null;
  return (
    <div>
      {callLog.map((row, i) => {
        const [kind, label] = chipFor(row);
        const isOpen = open === i;
        return (
          <React.Fragment key={`${row.taskId}${i}`}>
            <button
              type="button"
              className={`pdLogRow ${isOpen ? "open" : ""}`}
              aria-expanded={isOpen}
              onClick={() => setOpen(isOpen ? -1 : i)}
            >
              <span className="pdQueueName">{row.name}</span>
              <span className={`pdChip ${kind}`}>{label}</span>
              <span className="pdDur">
                {row.talkSeconds ? fmt(row.talkSeconds) : kind === "vm" && row.callSeconds ? fmt(row.callSeconds) : "—"}
              </span>
              <i className="material-symbols-outlined">{isOpen ? "expand_less" : "expand_more"}</i>
            </button>
            {isOpen && (
              <div className="pdLogDetail">
                <div className="pdLogMeta">
                  <span className="pdMuted">
                    {new Date(row.at).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                    {row.phone && ` · ${formatPhoneNumberIntl(row.phone) || row.phone}`}
                  </span>
                </div>
                {!row.notLogged && (
                  <label className="pdChangeOutcome">
                    Change outcome
                    <select
                      value={row.dispositionId || powerDialerStore.dispositionId(row.outcome)}
                      onChange={e => powerDialerStore.changeOutcome(row, e.target.value)}
                    >
                      <option value="" disabled>
                        Select
                      </option>
                      {dispositions.map(d => (
                        <option key={d.id} value={d.id}>
                          {d.displayName}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
                {row.suggestion?.summary && (
                  <div>
                    <b>AI summary</b> · {row.suggestion.summary}
                  </div>
                )}
                {row.note && (
                  <div>
                    <b>Your notes</b> · {row.note}
                  </div>
                )}
                <a className="pdLink" href={`/contacts/${row.contactId}/activity`} target="_blank" rel="noopener noreferrer">
                  Open contact
                </a>
              </div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
});

const UpNext = observer(() => {
  const { remainingQueue, noPhoneMode, state } = powerDialerStore;
  // The contact under "Calling next" is shown there, not again here.
  const next = [STATES.COUNTDOWN, STATES.PAUSED].includes(state) ? nextItem() : null;
  const remaining = remainingQueue.filter(q => q !== next);
  const upNext = remaining.slice(0, 3);
  if (!upNext.length) return null;
  return (
    <>
      <div className="pdSecTitle">Up next · {remaining.length}</div>
      <div>
        {upNext.map(q => (
          <div className="pdQueueRow" key={`${q.taskId}${q.pass || ""}`}>
            <span className="pdQueueName">{q.name}</span>
            {q.status === "finding" && (
              <span className="pdFinding">
                <span className="pdSpin" />
                Finding number…
              </span>
            )}
            {q.status === "not-found" && <span className="pdChip warn">No number found · will skip</span>}
            {q.status === "no-number" && <span className="pdChip">Find while calling</span>}
            {q.status === "ready" && (
              <>
                <span className="pdMuted">{contactClock(q.timeZone)?.label || "local time unknown"}</span>
                <button
                  type="button"
                  className="pdIconBtn"
                  aria-label={`Remove ${q.name} from this session`}
                  onClick={() => powerDialerStore.removeFromQueue(q)}
                >
                  <i className="material-symbols-outlined">close</i>
                </button>
              </>
            )}
          </div>
        ))}
      </div>
      {noPhoneMode === "find" && upNext.some(q => q.status !== "ready") && (
        <div className="pdSub">
          Numbers are looked up one at a time, two calls ahead, so there’s no wait when their turn comes.
        </div>
      )}
    </>
  );
});

const NOW = {
  [STATES.DIALING]: Dialing,
  [STATES.CONNECTED]: Connected,
  [STATES.VOICEMAIL]: Voicemail,
  [STATES.WRAPUP]: Wrapup,
  [STATES.AUTOLOG]: AutoLog,
  [STATES.COUNTDOWN]: Countdown,
  [STATES.PAUSED]: Paused
};

export const RunningView = observer(() => {
  const Now = NOW[powerDialerStore.state];
  return (
    <div className="pdBody">
      <Progress />
      {Now && <Now />}
      <UpNext />
      {powerDialerStore.callLog.length > 0 && <div className="pdSecTitle">This session · tap a call to review</div>}
      <SessionList />
    </div>
  );
});

export const RunningFoot = observer(() => {
  const { state, pauseRequested } = powerDialerStore;
  return (
    <>
      {state !== STATES.PAUSED && (
        <button
          type="button"
          className="pdGhostBtn"
          disabled={pauseRequested}
          onClick={powerDialerStore.requestPause}
        >
          <i className="material-symbols-outlined">pause</i>
          {pauseRequested ? "Pausing after this call" : "Pause"}
        </button>
      )}
      <button type="button" className="pdGhostBtn" onClick={() => askLeave(() => powerDialerStore.end("ended_by_rep"))}>
        End session
      </button>
    </>
  );
});

export const DoneView = observer(() => {
  const { summary } = powerDialerStore;
  const { counts, calls, skipped, startedAt, endedAt, notLogged } = summary;
  const noPhone = skipped.filter(s => s.reason === "no phone number");
  const minutes = Math.max(1, Math.round((endedAt - startedAt) / 60000));
  return (
    <div className="pdBody">
      <div className="pdTitle">Session done · {minutes} min</div>
      <div className="pdKpis">
        <div className="pdKpi">
          Dialed<b>{calls.length}</b>
        </div>
        <div className="pdKpi">
          Connected<b>{counts.connected}</b>
        </div>
        <div className="pdKpi">
          Voicemails<b>{counts.voicemail}</b>
        </div>
        <div className="pdKpi">
          Not logged<b className={notLogged ? "" : "ok"}>{notLogged}</b>
        </div>
      </div>
      {calls.length > 0 && <div className="pdSecTitle">All calls</div>}
      <SessionList />
      {noPhone.length > 0 && (
        <>
          <div className="pdSecTitle">Skipped · no phone number</div>
          <div>
            {noPhone.map(s => (
              <div className="pdQueueRow" key={s.taskId}>
                <span className="pdQueueName">{s.contact}</span>
                {s.found ? (
                  <span className="pdMuted">Number found</span>
                ) : (
                  <button
                    type="button"
                    className="pdLink"
                    disabled={s.finding}
                    onClick={() => powerDialerStore.findSkippedNumber(s)}
                  >
                    {s.finding ? "Finding…" : "Find phone number"}
                  </button>
                )}
              </div>
            ))}
          </div>
        </>
      )}
      {skipped.length > 0 && <div className="pdSub">Their call tasks stay due.</div>}
    </div>
  );
});

export const DoneFoot = observer(() => {
  const history = useHistory();
  const { sessionId, startedAt, endedAt } = powerDialerStore;
  return (
    <>
      <button
        type="button"
        className="pdGhostBtn"
        onClick={() => history.push(`/calls/overview?session=${sessionId}&from=${startedAt}&to=${endedAt}`)}
      >
        View in Calls
      </button>
      <button type="button" className="pdGhostBtn primary" onClick={() => history.push("/tasks")}>
        Back to tasks
      </button>
    </>
  );
});
